import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import PaywallModal from '../components/PaywallModal';
import ProcessVisualView from '../components/ProcessVisualView';
import OpportunityCard from '../components/opportunities/OpportunityCard';
import Tooltip from '../components/Tooltip';
import { usePlan } from '../context/PlanContext';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import { useOrgSwitchRedirect } from '../hooks/useOrgSwitchRedirect';

import {
  ArrowLeft,
  CaretDown,
  CaretUp,
  CircleNotch,
  Clock,
  CurrencyDollar,
  Eye,
  FloppyDisk,
  Info,
  Lightning,
  ListBullets,
  PencilSimple,
  Plus,
  Star,
  Target,
  Trash,
  TrendUp,
  UsersThree,
  X
} from '@phosphor-icons/react';
const ProcessDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userPlan, planLimits, isFreePlan } = usePlan();
  const { isReady, activeOrganization, orgLoading } = useOrgSwitchRedirect();
  
  const [process, setProcess] = useState(null);
  const [steps, setSteps] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [showAddStep, setShowAddStep] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [planLimitsFromAPI, setPlanLimitsFromAPI] = useState(null);
  const [totalOpportunityCount, setTotalOpportunityCount] = useState(0);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'visual'
  
  const [newStep, setNewStep] = useState({
    title: '',
    description: '',
    actor_role: '',
    system: '',
    avg_time_minutes: '',
    manual_or_automated: 'manual',
    pain_points: [],
    order_index: 1
  });

  useEffect(() => {
    if (!isReady) return;
    fetchProcessDetail();
    fetchDepartments();
  }, [id, isReady]);

  const fetchDepartments = async () => {
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/org/departments`,
        {},
        activeOrganization?.organization_id
      );
      if (response.ok) {
        const data = await response.json();
        setDepartments(data);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchProcessDetail = async () => {
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/org/processes/${id}`,
        {},
        activeOrganization?.organization_id
      );

      if (response.ok) {
        const data = await response.json();
        setProcess(data.process);
        setSteps(data.steps || []);
        setOpportunities(data.opportunities || []);
        setTotalOpportunityCount(data.opportunities?.length || 0);
        setNewStep({ ...newStep, order_index: (data.steps?.length || 0) + 1 });
      } else if (response.status === 404) {
        alert('Process not found or not accessible in this organization');
        navigate('/processes');
      } else {
        alert('Failed to load process');
        navigate('/processes');
      }
    } catch (error) {
      console.error('Error fetching process:', error);
      alert('Failed to load process');
      navigate('/processes');
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/org/processes/${id}/analyze`,
        {
          method: 'POST'
        },
        activeOrganization?.organization_id
      );

      if (response.ok) {
        const data = await response.json();
        setOpportunities(data.opportunities || []);
        setTotalOpportunityCount(data.total_opportunity_count || data.opportunities?.length || 0);
        setPlanLimitsFromAPI(data.plan_limits || null);
        
        // Refresh the page data to show updated opportunities
        await fetchProcessDetail();
        
        alert('Analysis complete! Opportunities have been generated.');
      } else {
        alert('Failed to analyze process');
      }
    } catch (error) {
      console.error('Error analyzing process:', error);
      alert('Failed to analyze process');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleToggleFavorite = async (opportunityId) => {
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/opportunities/${opportunityId}/toggle-favorite`,
        {
          method: 'POST'
        },
        activeOrganization?.organization_id
      );

      if (response.ok) {
        const data = await response.json();
        // Update local state
        setOpportunities(prev => prev.map(opp => 
          opp.id === opportunityId ? { ...opp, is_favorite: data.is_favorite } : opp
        ));
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const handleAddStep = async () => {
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/org/processes/${id}/steps`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...newStep,
            avg_time_minutes: newStep.avg_time_minutes ? parseFloat(newStep.avg_time_minutes) : null
          })
        },
        activeOrganization?.organization_id
      );

      if (response.ok) {
        fetchProcessDetail();
        setShowAddStep(false);
        setNewStep({
          title: '',
          description: '',
          actor_role: '',
          system: '',
          avg_time_minutes: '',
          manual_or_automated: 'manual',
          pain_points: [],
          order_index: steps.length + 1
        });
      } else {
        alert('Failed to add step');
      }
    } catch (error) {
      console.error('Error adding step:', error);
      alert('Failed to add step');
    }
  };

  const handleUpdateStep = async (stepId, updates) => {
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/org/processes/${id}/steps/${stepId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updates)
        },
        activeOrganization?.organization_id
      );

      if (response.ok) {
        fetchProcessDetail();
        setEditingStep(null);
      } else {
        alert('Failed to update step');
      }
    } catch (error) {
      console.error('Error updating step:', error);
      alert('Failed to update step');
    }
  };

  const handleDeleteStep = async (stepId) => {
    if (!window.confirm('Are you sure you want to delete this step?')) return;

    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/org/processes/${id}/steps/${stepId}`,
        {
          method: 'DELETE'
        },
        activeOrganization?.organization_id
      );

      if (response.ok) {
        fetchProcessDetail();
      } else {
        alert('Failed to delete step');
      }
    } catch (error) {
      console.error('Error deleting step:', error);
      alert('Failed to delete step');
    }
  };

  const handleReorderStep = async (stepId, currentIndex, direction) => {
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 1 || newIndex > steps.length) return;

    await handleUpdateStep(stepId, { order_index: newIndex });
  };

  // Wrappers for Visual View
  const handleVisualStepUpdate = async (stepId, updates) => {
    await handleUpdateStep(stepId, updates);
  };

  const handleVisualStepDelete = async (stepId) => {
    if (window.confirm('Are you sure you want to delete this step?')) {
      await handleDeleteStep(stepId);
    }
  };

  const handleVisualStepCreate = async (stepData) => {
    setNewStep(stepData);
    await handleAddStep();
    setNewStep({
      title: '',
      description: '',
      actor_role: '',
      system: '',
      avg_time_minutes: '',
      manual_or_automated: 'manual',
      pain_points: [],
      order_index: steps.length + 1
    });
  };

  const handleVisualStepReorder = async (stepId, direction) => {
    const step = steps.find(s => s.id === stepId);
    if (step) {
      await handleReorderStep(stepId, step.order_index, direction);
    }
  };

  if (orgLoading || !activeOrganization || loading) {
    return (
      <div className="min-h-screen bg-[#161B26] flex items-center justify-center">
        <CircleNotch size={48} className="text-[#6366F1] animate-spin" />
      </div>
    );
  }

  if (!process) {
    return (
      <div className="min-h-screen bg-[#161B26] flex items-center justify-center">
        <div className="text-center">
          <p className="text-white text-lg mb-4">Process not found for this organization</p>
          <button
            onClick={() => navigate('/processes')}
            className="px-6 py-3 rounded-full bg-[#6366F1] hover:bg-[#5558E6] text-white transition-colors"
          >
            Back to Process Library
          </button>
        </div>
      </div>
    );
  }

  const department = departments.find(d => d.id === process.department_id);

  return (
      <>
      <div className="max-w-7xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate('/processes')}
            className="flex items-center gap-2 text-[#A0AEC0] hover:text-white transition-colors mb-6"
          >
            <ArrowLeft size={20} />
            Back to Process Library
          </button>

          {/* Process Summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass rounded-3xl p-8 mb-8"
          >
            <div className="flex items-start justify-between mb-6">
              <div className="flex-1">
                <h1 className="text-4xl font-bold text-white mb-2">{process.name}</h1>
                <div className="flex items-center gap-4 text-[#A0AEC0]">
                  {department && <span>{department.name}</span>}
                  {process.owner_name && (
                    <span>• {process.owner_name} {process.owner_role && `(${process.owner_role})`}</span>
                  )}
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => navigate(`/processes/${id}/edit`)}
                  className="px-4 py-3 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-white transition-all"
                >
                  <PencilSimple size={20} />
                </button>
                <button
                  onClick={handleAnalyze}
                  disabled={analyzing || steps.length === 0}
                  className="rounded-full bg-[#6366F1] hover:bg-[#5558E6] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title={steps.length === 0 ? 'Add steps to the process before running analysis' : ''}
                >
                  <span className="flex items-center gap-2 px-6 py-3 text-white font-bold">
                    {analyzing ? (
                      <><CircleNotch size={20} className="animate-spin" /> Analyzing...</>
                    ) : (
                      <><Lightning size={20} /> Run Opportunity Analysis</>
                    )}
                  </span>
                </button>
              </div>
            </div>

            {process.objective && (
              <div className="mb-8">
                <h3 className="text-lg font-bold text-white mb-3">Objective</h3>
                <p className="text-[#A0AEC0] leading-relaxed">{process.objective}</p>
              </div>
            )}

            {process.description && (
              <div className="mb-8">
                <h3 className="text-lg font-bold text-white mb-3">Description</h3>
                <p className="text-[#A0AEC0] leading-relaxed">{process.description}</p>
              </div>
            )}

            {/* Metrics Grid */}
            <div className="grid md:grid-cols-4 gap-6 mb-6">
              <div className="glass rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Clock size={16} className="text-[#6366F1]" />
                  <p className="text-[#A0AEC0] text-sm">Frequency</p>
                </div>
                <p className="text-white font-semibold">{process.frequency || 'Not set'}</p>
              </div>

              <div className="glass rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <TrendUp size={16} className="text-[#14F195]" />
                  <p className="text-[#A0AEC0] text-sm">Volume</p>
                </div>
                <p className="text-white font-semibold">
                  {process.volume_per_period ? `${process.volume_per_period} / period` : 'Not set'}
                </p>
              </div>

              <div className="glass rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Clock size={16} className="text-[#EC4899]" />
                  <p className="text-[#A0AEC0] text-sm">Cycle Time</p>
                </div>
                <p className="text-white font-semibold">
                  {process.avg_cycle_time_minutes ? `${process.avg_cycle_time_minutes} min` : 'Not set'}
                </p>
              </div>

              <div className="glass rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Target size={16} className="text-[#F59E0B]" />
                  <p className="text-[#A0AEC0] text-sm">Error Rate</p>
                </div>
                <p className="text-white font-semibold">
                  {process.error_rate_percent ? `${process.error_rate_percent}%` : 'Not set'}
                </p>
              </div>
            </div>

            {/* Systems Used */}
            {process.systems_used && process.systems_used.length > 0 && (
              <div className="mb-6">
                <h3 className="text-white font-semibold mb-2">Systems Used</h3>
                <div className="flex flex-wrap gap-2">
                  {process.systems_used.map((system, i) => (
                    <span key={i} className="px-3 py-1 rounded-full bg-[#6366F1]/20 text-[#6366F1] text-sm">
                      {system}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Pain Points */}
            {process.pain_points && process.pain_points.length > 0 && (
              <div>
                <h3 className="text-white font-semibold mb-2">Pain Points</h3>
                <div className="flex flex-wrap gap-2">
                  {process.pain_points.map((pain, i) => (
                    <span key={i} className="px-3 py-1 rounded-full bg-[#EF4444]/20 text-[#EF4444] text-sm">
                      {pain}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </motion.div>

          {/* Process Steps */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass rounded-3xl p-8 mb-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Process Steps</h2>
              
              <div className="flex items-center gap-4">
                {/* View Mode Toggle - always in same position */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setViewMode('list')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      viewMode === 'list'
                        ? 'bg-[#6366F1] text-white'
                        : 'glass text-[#A0AEC0] hover:text-white'
                    }`}
                  >
                    <ListBullets size={16} />
                    ListBullets View
                  </button>
                  <button
                    onClick={() => setViewMode('visual')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      viewMode === 'visual'
                        ? 'bg-[#6366F1] text-white'
                        : 'glass text-[#A0AEC0] hover:text-white'
                    }`}
                  >
                    <Eye size={16} />
                    Visual View
                  </button>
                </div>

                {/* Add Step Button - always visible, intelligent label */}
                <button
                  onClick={() => setShowAddStep(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-white transition-all min-w-[160px] justify-center"
                >
                  <Plus size={20} />
                  {steps.length === 0 ? 'Add First Step' : 'Add Step'}
                </button>
              </div>
            </div>

            {/* ListBullets View */}
            {viewMode === 'list' && (
              <>
                {/* Add Step Form */}
                {showAddStep && (
              <div className="mb-6 p-6 rounded-2xl bg-[#6366F1]/10 border border-[#6366F1]/30">
                <h3 className="text-white font-semibold mb-4">New Step</h3>
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="Step Title *"
                    value={newStep.title}
                    onChange={(e) => setNewStep({ ...newStep, title: e.target.value })}
                    className="w-full px-4 py-2 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)]"
                  />
                  <textarea
                    placeholder="Description"
                    value={newStep.description}
                    onChange={(e) => setNewStep({ ...newStep, description: e.target.value })}
                    className="w-full px-4 py-2 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)] resize-none"
                    rows={2}
                  />
                  <div className="grid md:grid-cols-3 gap-4">
                    <input
                      type="text"
                      placeholder="Actor Role"
                      value={newStep.actor_role}
                      onChange={(e) => setNewStep({ ...newStep, actor_role: e.target.value })}
                      className="px-4 py-2 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)]"
                    />
                    <input
                      type="text"
                      placeholder="System"
                      value={newStep.system}
                      onChange={(e) => setNewStep({ ...newStep, system: e.target.value })}
                      className="px-4 py-2 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)]"
                    />
                    <input
                      type="number"
                      placeholder="Time (minutes)"
                      value={newStep.avg_time_minutes}
                      onChange={(e) => setNewStep({ ...newStep, avg_time_minutes: e.target.value })}
                      className="px-4 py-2 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)]"
                    />
                  </div>
                  <select
                    value={newStep.manual_or_automated}
                    onChange={(e) => setNewStep({ ...newStep, manual_or_automated: e.target.value })}
                    className="w-full px-4 py-2 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)]"
                  >
                    <option value="manual">Manual</option>
                    <option value="semi-automated">Semi-Automated</option>
                    <option value="automated">Automated</option>
                  </select>
                  <div className="flex gap-2">
                    <button
                      onClick={handleAddStep}
                      disabled={!newStep.title}
                      className="px-4 py-2 rounded-full bg-[#14F195] text-[#161B26] font-semibold disabled:opacity-50"
                    >
                      <FloppyDisk size={16} className="inline mr-2" />
                      Save Step
                    </button>
                    <button
                      onClick={() => setShowAddStep(false)}
                      className="px-4 py-2 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-white"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Steps ListBullets */}
            {steps.length === 0 ? (
              <div className="text-center py-12 text-[#A0AEC0]">
                <p>No steps defined yet. Click &quot;Add Step&quot; to get started.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {steps.map((step) => (
                  <div key={step.id} className="p-6 rounded-2xl glass hover:bg-[rgba(255,255,255,0.05)] transition-all">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-8 h-8 rounded-full bg-[#6366F1]/20 flex items-center justify-center text-[#6366F1] font-bold text-sm">
                            {step.order_index}
                          </div>
                          <h3 className="text-xl font-bold text-white">{step.title}</h3>
                        </div>
                        {step.description && (
                          <p className="text-[#A0AEC0] mb-3">{step.description}</p>
                        )}
                        <div className="flex flex-wrap gap-4 text-sm">
                          {step.actor_role && (
                            <span className="text-[#A0AEC0]">
                              <UsersThree size={14} className="inline mr-1" />
                              {step.actor_role}
                            </span>
                          )}
                          {step.system && (
                            <span className="text-[#A0AEC0]">
                              <Target size={14} className="inline mr-1" />
                              {step.system}
                            </span>
                          )}
                          {step.avg_time_minutes && (
                            <span className="text-[#A0AEC0]">
                              <Clock size={14} className="inline mr-1" />
                              {step.avg_time_minutes} min
                            </span>
                          )}
                          {step.manual_or_automated && (
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              step.manual_or_automated === 'automated' ? 'bg-[#14F195]/20 text-[#14F195]' :
                              step.manual_or_automated === 'semi-automated' ? 'bg-[#F59E0B]/20 text-[#F59E0B]' :
                              'bg-[#A0AEC0]/20 text-[#A0AEC0]'
                            }`}>
                              {step.manual_or_automated}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleReorderStep(step.id, step.order_index, 'up')}
                          disabled={step.order_index === 1}
                          className="p-2 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-white disabled:opacity-30"
                        >
                          <CaretUp size={16} />
                        </button>
                        <button
                          onClick={() => handleReorderStep(step.id, step.order_index, 'down')}
                          disabled={step.order_index === steps.length}
                          className="p-2 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-white disabled:opacity-30"
                        >
                          <CaretDown size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteStep(step.id)}
                          className="p-2 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-[#EF4444]"
                        >
                          <Trash size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
              </>
            )}

            {/* Visual View */}
            {viewMode === 'visual' && (
              <ProcessVisualView
                process={process}
                steps={steps}
                onStepUpdate={handleVisualStepUpdate}
                onStepDelete={handleVisualStepDelete}
                onStepCreate={handleVisualStepCreate}
                onStepReorder={handleVisualStepReorder}
                onAddStepClick={() => setShowAddStep(true)}
              />
            )}
          </motion.div>

          {/* Opportunities */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass rounded-3xl p-8"
          >
            <div className="flex items-center gap-2 mb-6">
              <h2 className="text-2xl font-bold text-white">AI-Identified Opportunities</h2>
              <Tooltip text="Automation opportunities identified by AI analysis based on process steps, pain points, and potential savings.">
                <Info size={18} className="text-[#718096] cursor-help" />
              </Tooltip>
            </div>

            {opportunities.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-[#6366F1]/20 flex items-center justify-center mx-auto mb-4">
                  <Lightning size={32} className="text-[#6366F1]" />
                </div>
                <p className="text-[#A0AEC0] mb-4">
                  No opportunities identified yet. Click &quot;Run Opportunity Analysis&quot; to discover automation opportunities.
                </p>
              </div>
            ) : (
              <div>
                {isFreePlan() && totalOpportunityCount > 1 && (
                  <div className="mb-6 p-4 rounded-2xl bg-[#6366F1]/10 border border-[#6366F1]/30">
                    <p className="text-white font-medium">
                      Showing 1 of {totalOpportunityCount} opportunities.
                      <button
                        onClick={() => setShowPaywall(true)}
                        className="ml-2 text-[#14F195] hover:underline"
                      >
                        Upgrade to Pro
                      </button>
                      {' '}to unlock all opportunities.
                    </p>
                  </div>
                )}

                <div className="space-y-6">
                  {opportunities
                    .filter(opp => opp.title && opp.description && (opp.hours_saved_monthly > 0 || opp.cost_savings_annual > 0))
                    .slice(0, isFreePlan() ? 1 : opportunities.length)
                    .map((opp, index) => (
                      <OpportunityCard
                        key={opp.id}
                        id={opp.id}
                        title={opp.title}
                        description={opp.description}
                        impact={opp.impact}
                        effort={opp.effort}
                        priority_score={opp.priority_score}
                        roi={opp.roi}
                        monthly_savings={opp.cost_savings_monthly}
                        annual_savings={opp.cost_savings_annual}
                        time_saved={opp.hours_saved_monthly}
                        status={opp.status}
                        category={opp.category}
                        source="process"
                        onViewDetails={() => navigate(`/opportunities/${opp.id}`)}
                        index={index}
                      />
                    ))}
                </div>

                {isFreePlan() && totalOpportunityCount > 1 && (
                  <div className="mt-6 text-center">
                    <button
                      onClick={() => setShowPaywall(true)}
                      className="rounded-full bg-[#6366F1] hover:bg-[#5558E6] transition-colors"
                    >
                      <span className="flex items-center gap-2 px-6 py-3 text-white font-bold">
                        Unlock {totalOpportunityCount - 1} More Opportunities
                      </span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </div>
        
        <PaywallModal 
          isOpen={showPaywall} 
          onClose={() => setShowPaywall(false)}
          opportunityCount={totalOpportunityCount}
        />
      </>
  );
};

export default ProcessDetail;
